/**
 * ExpenseBookingModal — M4b Ausgaben-Frontend
 *
 * Presents a pre-filled confirmation modal before an incoming ZUGFeRD invoice
 * is recorded as a bookable expense. The human ALWAYS confirms — no silent booking.
 *
 * Key design invariants (from architecture-m4.md §4 + plan-feedback-solar-m4.md):
 *   - needs_review taxScheme blocks booking until user selects a concrete scheme.
 *   - §13b/IGL: self-computed VAT at 19 % default, user-overrideable to 7 %.
 *     NEVER labelled "gesichert" — the user explicitly confirms the rate.
 *   - vatOwedCents and vatDeductibleCents are displayed separately (two fields).
 *   - §14-incompleteness banner is NEUTRAL: "ggf. nicht möglich" — never "gesichert/GoBD-konform".
 *   - import_third_country shows a specific customs-document notice.
 *   - Non-EUR: displayed with a warning; user is responsible for manual check.
 *   - All monetary display: Cent → Euro via centsToEuros, formatted as EUR.
 *
 * @module ExpenseBookingModal
 */

import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  IconClose,
  IconWarning,
  IconInfo,
  IconSuccess,
  IconRejected,
  IconHelp,
} from '../../icons';

import { useAuth } from '../../context/AuthContext';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import { ReceivedInvoice } from '../../types/receivedInvoiceTypes';
import {
  TaxScheme,
  ExpenseDraft,
  CreateExpenseInput,
} from '../../types/expenseTypes';
import {
  mapReceivedInvoiceToExpenseDraft,
  ExpenseSellerContext,
} from '../../utils/expenseTax';
import { createExpense } from '../../api/expensesApi';
import { centsToEuros } from '../../utils/moneyUtils';
import {
  computeEffectiveVatOwedCents,
  computeEffectiveVatDeductible,
  computeEffectiveBreakdown,
} from './expenseDeriveShared';

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_TAX_SCHEMES: TaxScheme[] = [
  'standard',
  'reverse_charge_13b',
  'intra_community_6a',
  'import_third_country',
  'small_business_19',
  'zero_exempt',
  'needs_review',
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpenseBookingModalProps {
  /** Whether the modal is visible. */
  open: boolean;
  /** The incoming ZUGFeRD invoice to book as an expense. */
  invoice: ReceivedInvoice;
  /** Called when the modal is dismissed without booking. */
  onClose: () => void;
  /**
   * Called after a successful booking with the new Firestore document ID.
   * The parent should use this to mark the invoice as "already booked".
   */
  onBooked: (expenseId: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Formats integer cents as a EUR currency string (de-DE locale).
 */
function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

// The VAT-derivation helpers (computeEffectiveVatOwedCents,
// computeEffectiveVatDeductible, computeEffectiveBreakdown) live in
// ./expenseDeriveShared so M13a-4 can reuse them instead of copying.

// ─── ExpenseBookingModal ──────────────────────────────────────────────────────

/**
 * Booking modal for converting a parsed ZUGFeRD incoming invoice into an expense.
 *
 * Pre-fills all fields from `mapReceivedInvoiceToExpenseDraft`. The user may
 * override the taxScheme (always) and, for §13b/IGL, the VAT rate (19/7 %).
 * A human must confirm by clicking "Ausgabe buchen" or "Als Entwurf speichern".
 */
const ExpenseBookingModal: React.FC<ExpenseBookingModalProps> = ({
  open,
  invoice,
  onClose,
  onBooked,
}) => {
  const { t } = useTranslation('expenses');
  const { sellerData } = useAuth();
  // M11b-3: advisor (read-only) darf keine Ausgabe buchen/als Entwurf speichern.
  const isReadOnly = useIsReadOnly();

  // ── Compute initial draft from the incoming invoice ──────────────────────
  const sellerContext: ExpenseSellerContext = {
    isSmallBusiness: sellerData?.isSmallBusiness,
  };
  const draft: ExpenseDraft = mapReceivedInvoiceToExpenseDraft(
    invoice.data,
    sellerContext,
  );

  const userIsFullyVatDeductible = sellerContext.isSmallBusiness === false;

  // ── Local user-overrideable state ────────────────────────────────────────
  const [localTaxScheme, setLocalTaxScheme] = useState<TaxScheme>(draft.taxScheme);
  const [localVatRate, setLocalVatRate] = useState<19 | 7>(19);
  const [category, setCategory] = useState('');
  const [skrAccount, setSkrAccount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state when invoice changes (modal re-opened for a different invoice)
  useEffect(() => {
    if (open) {
      const freshDraft = mapReceivedInvoiceToExpenseDraft(invoice.data, sellerContext);
      setLocalTaxScheme(freshDraft.taxScheme);
      setLocalVatRate(19);
      setCategory('');
      setSkrAccount('');
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice.id]);

  // ── Derived / computed values ────────────────────────────────────────────
  const isReverseCharge =
    localTaxScheme === 'reverse_charge_13b' || localTaxScheme === 'intra_community_6a';

  const effectiveVatOwedCents = computeEffectiveVatOwedCents(
    localTaxScheme,
    localVatRate,
    draft.netCents,
    draft.vatOwedCents,
  );

  const effectiveVatDeductible = computeEffectiveVatDeductible(
    localTaxScheme,
    effectiveVatOwedCents,
    draft.completenessStatus,
    userIsFullyVatDeductible,
  );

  const effectiveVatDeductibleCents = effectiveVatDeductible ? effectiveVatOwedCents : 0;

  const effectiveBreakdown = computeEffectiveBreakdown(
    draft.vatBreakdown,
    localTaxScheme,
    localVatRate,
    effectiveVatDeductible,
  );

  // Booking is blocked when taxScheme is still needs_review (user must choose)
  const canBook = localTaxScheme !== 'needs_review';

  // ── Missing field labels for the §14 completeness banner ────────────────
  const missingFieldLabels = draft.missingMandatoryFields
    .map((field) => t(`missingFields.${field}`, field))
    .join(', ');

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleTaxSchemeChange = useCallback(
    (scheme: TaxScheme) => {
      setLocalTaxScheme(scheme);
      // Reset vatRate to 19 when switching to/from §13b/IGL
      if (scheme === 'reverse_charge_13b' || scheme === 'intra_community_6a') {
        setLocalVatRate(19);
      }
    },
    [],
  );

  const handleSave = useCallback(
    async (status: 'booked' | 'draft') => {
      if (submitting) return;
      if (status === 'booked' && !canBook) return;

      setSubmitting(true);
      try {
        const input: CreateExpenseInput = {
          supplierName: draft.supplierName,
          supplierVatId: draft.supplierVatId,
          supplierTaxId: draft.supplierTaxId,
          invoiceNumber: draft.invoiceNumber,
          documentTypeCode: draft.documentTypeCode,

          netCents: draft.netCents,
          vatOwedCents: effectiveVatOwedCents,
          vatDeductibleCents: effectiveVatDeductibleCents,
          grossCents: draft.grossCents,
          vatRate: isReverseCharge ? localVatRate : draft.vatRate,
          vatBreakdown: effectiveBreakdown,
          roundingDiffCents: draft.roundingDiffCents,

          taxScheme: localTaxScheme,
          // Confirmed whenever human clicks save (they've reviewed the modal)
          taxSchemeConfirmedByUser: true,
          vatDeductible: effectiveVatDeductible,

          completenessStatus: draft.completenessStatus,
          missingMandatoryFields: draft.missingMandatoryFields,

          expenseDate: draft.expenseDate,
          dueDate: draft.dueDate,
          serviceDate: draft.serviceDate,

          category: category.trim() || null,
          skrAccount: skrAccount.trim() || null,

          sourceReceivedInvoiceId: invoice.id,
          nonEurCurrency: draft.nonEurCurrency,

          // IPA-4 (Incoming-PDF-Archive): bind the archived original PDF (if any)
          // to the booked expense — the only place M7b reads for the DATEV
          // Belege-ZIP (expenses.receiptStorageRef). Conditional-Spread: bei
          // Legacy-/flag-off-Rechnungen (invoice.pdfStorageRef ist absent) MUSS
          // der Key ganz WEGGELASSEN werden — ein materialisierter undefined-Wert
          // ließe addDoc hart werfen (Firestore lehnt undefined-Feldwerte ab).
          ...(invoice.pdfStorageRef ? { receiptStorageRef: invoice.pdfStorageRef } : {}),
          source: 'zugferd',

          status,
        };

        const expenseId = await createExpense(input);
        toast.success(t('modal.success'));
        onBooked(expenseId);
      } catch (err) {
        console.error('ExpenseBookingModal: createExpense failed', err);
        toast.error(t('modal.errorSave'));
      } finally {
        setSubmitting(false);
      }
    },
    [
      submitting,
      canBook,
      draft,
      effectiveVatOwedCents,
      effectiveVatDeductibleCents,
      effectiveVatDeductible,
      effectiveBreakdown,
      isReverseCharge,
      localVatRate,
      localTaxScheme,
      category,
      skrAccount,
      invoice.id,
      t,
      onBooked,
    ],
  );

  if (!open) return null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="expense-modal-title"
    >
      <div className="w-full max-w-lg bg-surface-raised border border-hairline rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h2 id="expense-modal-title" className="text-primary font-semibold text-base">
            {t('modal.title')}
          </h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-muted hover:text-primary transition-colors disabled:opacity-40 p-1 -mr-1"
            aria-label={t('modal.cancel')}
          >
            <IconClose />
          </button>
        </div>

        {/* ── Scrollable body ──────────────────────────────────────────────── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Non-EUR warning */}
          {draft.nonEurCurrency && (
            <div className="flex items-start gap-2 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg px-3 py-2.5 text-[var(--tone-warning-fg)] text-xs">
              <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
              <span>{t('modal.nonEurWarning', { currency: draft.nonEurCurrency })}</span>
            </div>
          )}

          {/* needs_review taxScheme warning */}
          {localTaxScheme === 'needs_review' && (
            <div className="flex items-start gap-2 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg px-3 py-2.5 text-[var(--tone-danger-fg)] text-xs">
              <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
              <span>{t('modal.needsReviewWarning')}</span>
            </div>
          )}

          {/* §14 completeness banner (neutral wording) */}
          {draft.completenessStatus === 'incomplete' && (
            <div className="flex items-start gap-2 bg-[var(--tone-caution-bg)] border border-[var(--tone-caution-border)] rounded-lg px-3 py-2.5 text-[var(--tone-caution-fg)] text-xs">
              <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                {t('modal.incompleteWarning', { fields: missingFieldLabels || '—' })}
              </span>
            </div>
          )}
          {draft.completenessStatus === 'needs_review' && (
            <div className="flex items-start gap-2 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg px-3 py-2.5 text-[var(--tone-info-fg)] text-xs">
              <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
              <span>{t('modal.needsReviewCompleteness')}</span>
            </div>
          )}

          {/* import_third_country EUSt notice */}
          {localTaxScheme === 'import_third_country' && (
            <div className="flex items-start gap-2 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg px-3 py-2.5 text-[var(--tone-info-fg)] text-xs">
              <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
              <span>{t('modal.importNotice')}</span>
            </div>
          )}

          {/* §13b/IGL self-computed notice */}
          {isReverseCharge && (
            <div className="flex items-start gap-2 bg-[var(--tone-special-bg)] border border-[var(--tone-special-border)] rounded-lg px-3 py-2.5 text-[var(--tone-special-fg)] text-xs">
              <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
              <span>{t('modal.selfComputedNotice')}</span>
            </div>
          )}

          {/* Supplier info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-faint text-xs mb-0.5">{t('modal.supplier')}</p>
              <p className="text-primary font-medium">{draft.supplierName || '—'}</p>
            </div>
            <div>
              <p className="text-faint text-xs mb-0.5">{t('modal.invoiceNumber')}</p>
              <p className="text-primary font-mono text-xs">{draft.invoiceNumber || '—'}</p>
            </div>
            <div>
              <p className="text-faint text-xs mb-0.5">{t('modal.invoiceDate')}</p>
              <p className="text-primary text-xs">{draft.expenseDate || '—'}</p>
            </div>
            {draft.serviceDate && (
              <div>
                <p className="text-faint text-xs mb-0.5">{t('modal.serviceDate')}</p>
                <p className="text-primary text-xs">{draft.serviceDate}</p>
              </div>
            )}
            {draft.dueDate && (
              <div>
                <p className="text-faint text-xs mb-0.5">{t('modal.dueDate')}</p>
                <p className="text-primary text-xs">{draft.dueDate}</p>
              </div>
            )}
          </div>

          <hr className="border-hairline" />

          {/* Amounts */}
          <div>
            <p className="text-muted text-xs font-medium uppercase tracking-wide mb-2">
              {t('modal.amounts')}
            </p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted">{t('modal.net')}</span>
                <span className="text-primary font-mono">{formatCents(draft.netCents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted">
                  {t('modal.vatOwed')}
                  {isReverseCharge && (
                    <span className="text-[var(--tone-special-fg)] ml-1 text-xs">(§ 13b/igE)</span>
                  )}
                </span>
                <span className="text-primary font-mono">{formatCents(effectiveVatOwedCents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted">{t('modal.vatDeductibleLabel')}</span>
                <span
                  className={`font-mono ${effectiveVatDeductible ? 'text-[var(--tone-success-fg)]' : 'text-faint'}`}
                >
                  {formatCents(effectiveVatDeductibleCents)}
                </span>
              </div>
              <div className="flex justify-between text-sm font-semibold border-t border-hairline pt-1.5">
                <span className="text-secondary">{t('modal.gross')}</span>
                <span className="text-primary font-mono">{formatCents(draft.grossCents)}</span>
              </div>
              {draft.roundingDiffCents !== 0 && (
                <div className="flex justify-between text-xs text-[var(--tone-warning-fg)]">
                  <span>{t('modal.roundingDiff')}</span>
                  <span className="font-mono">{formatCents(draft.roundingDiffCents)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Multi-rate VAT breakdown */}
          {effectiveBreakdown.length > 1 && (
            <div>
              <p className="text-muted text-xs font-medium uppercase tracking-wide mb-2">
                {t('modal.multiRateTitle')}
              </p>
              <table className="w-full text-xs text-secondary">
                <thead>
                  <tr className="text-faint text-left">
                    <th className="pb-1 font-normal">{t('modal.vatBreakdown.categoryCode')}</th>
                    <th className="pb-1 font-normal text-right">{t('modal.vatBreakdown.rate')}</th>
                    <th className="pb-1 font-normal text-right">{t('modal.vatBreakdown.net')}</th>
                    <th className="pb-1 font-normal text-right">{t('modal.vatBreakdown.owed')}</th>
                    <th className="pb-1 font-normal text-right">{t('modal.vatBreakdown.deductible')}</th>
                  </tr>
                </thead>
                <tbody>
                  {effectiveBreakdown.map((b, i) => (
                    <tr key={i} className="border-t border-hairline">
                      <td className="py-1 font-mono">{b.categoryCode}</td>
                      <td className="py-1 text-right">{b.taxRate} %</td>
                      <td className="py-1 text-right font-mono">{formatCents(b.netCents)}</td>
                      <td className="py-1 text-right font-mono">{formatCents(b.vatOwedCents)}</td>
                      <td
                        className={`py-1 text-right font-mono ${b.vatDeductibleCents > 0 ? 'text-[var(--tone-success-fg)]' : 'text-faint'}`}
                      >
                        {formatCents(b.vatDeductibleCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <hr className="border-hairline" />

          {/* Tax scheme selector */}
          <div>
            <label className="block text-xs text-muted mb-1" htmlFor="taxSchemeSelect">
              {t('modal.taxSchemeLabel')}
            </label>
            <select
              id="taxSchemeSelect"
              value={localTaxScheme}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => handleTaxSchemeChange(e.target.value as TaxScheme)}
              disabled={submitting}
              className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 cursor-pointer"
            >
              {ALL_TAX_SCHEMES.map((scheme) => (
                <option key={scheme} value={scheme}>
                  {t(`taxScheme.${scheme}`)}
                </option>
              ))}
            </select>
            <p className="text-faint text-xs mt-1">{t('modal.taxSchemeHelp')}</p>
          </div>

          {/* VAT rate selector — only for §13b/IGL */}
          {isReverseCharge && (
            <div>
              <label className="block text-xs text-muted mb-1" htmlFor="vatRateSelect">
                {t('modal.vatRateLabel')}
              </label>
              <select
                id="vatRateSelect"
                value={localVatRate}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setLocalVatRate(Number(e.target.value) as 19 | 7)}
                disabled={submitting}
                className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 cursor-pointer"
              >
                <option value={19}>{t('modal.vatRate19')}</option>
                <option value={7}>{t('modal.vatRate7')}</option>
              </select>
              <p className="text-faint text-xs mt-1">{t('modal.vatRateHelp')}</p>
            </div>
          )}

          {/* vatDeductible indicator */}
          <div className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${
            effectiveVatDeductible
              ? 'bg-[var(--tone-success-bg)] border-[var(--tone-success-border)] text-[var(--tone-success-fg)]'
              : 'bg-[rgb(var(--surface-raised-rgb)/0.6)] border-hairline text-muted'
          }`}>
            {effectiveVatDeductible ? (
              <IconSuccess className="flex-shrink-0" />
            ) : (
              <IconRejected className="flex-shrink-0" />
            )}
            <span>
              {effectiveVatDeductible
                ? t('modal.vatDeductibleInfo')
                : sellerContext.isSmallBusiness
                  ? t('modal.smallBusinessNotice')
                  : t('modal.vatNotDeductibleInfo')}
            </span>
          </div>

          <hr className="border-hairline" />

          {/* Optional fields: category + skrAccount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1" htmlFor="expenseCategory">
                {t('modal.categoryLabel')}
              </label>
              <input
                id="expenseCategory"
                type="text"
                value={category}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setCategory(e.target.value)}
                disabled={submitting}
                placeholder={t('modal.categoryPlaceholder')}
                className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
              />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1" htmlFor="expenseSkrAccount">
                {t('modal.skrAccountLabel')}
              </label>
              <input
                id="expenseSkrAccount"
                type="text"
                value={skrAccount}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSkrAccount(e.target.value)}
                disabled={submitting}
                placeholder={t('modal.skrAccountPlaceholder')}
                className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
              />
            </div>
          </div>
        </div>

        {/* ── Footer / actions ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-hairline bg-[rgb(var(--surface-base-rgb)/0.4)]">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-secondary hover:text-primary transition-colors disabled:opacity-40"
          >
            {t('modal.cancel')}
          </button>
          {/* Speichern/Buchen — M11b-3: für advisor (read-only) ausgeblendet */}
          {!isReadOnly && (
          <>
          <button
            type="button"
            onClick={() => handleSave('draft')}
            disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg border border-strong text-secondary hover:border-gray-500 hover:text-primary transition-colors disabled:opacity-40"
          >
            {submitting ? t('modal.saving') : t('modal.saveAsDraft')}
          </button>
          <button
            type="button"
            onClick={() => handleSave('booked')}
            disabled={submitting || !canBook}
            className="px-5 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {!canBook && <IconHelp size={12} />}
            {submitting ? t('modal.saving') : t('modal.book')}
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseBookingModal;
